/** Shots and bombs in flight, and everything that can touch anything else (docs/spec/02 §2, §8). */

import { damageEnemy, hitShip, shipXs } from './combat';
import { hitEmpress } from './empress';
import { BOMB, ENEMY_HIT, FIELD, FIGHTER, NOVA, SHOT } from './tuning';
import type { Enemy, GameState, Shot } from './types';

/** Stands in a piercing shot's list for "has struck the Empress". */
const EMPRESS_ID = -1;

export function updateShots(s: GameState): void {
  s.shots = s.shots.filter((shot) => {
    shot.x += shot.vx;
    shot.y += shot.vy;
    return shot.y > -8 && shot.x > -4 && shot.x < FIELD.w + 4;
  });
}

export function updateBombs(s: GameState): void {
  s.bombs = s.bombs.filter((b) => {
    b.x += b.vx;
    b.y += b.vy;
    return b.y < FIELD.h + 8 && b.x > -8 && b.x < FIELD.w + 8;
  });
}

function hitHalf(e: Enemy): number {
  return e.kind === 'monarch' ? ENEMY_HIT.monarch : ENEMY_HIT.small;
}

/** On screen and really there (a trio's followers wait off-stage at the start of their path). */
function targetable(e: Enemy): boolean {
  return !e.gone && e.y > 0 && e.dist >= 0;
}

function count(s: GameState, shot: Shot): void {
  if (shot.scored) return;
  shot.scored = true;
  s.stats.hits++;
}

/** True if the shot is spent. */
function shotStrikes(s: GameState, shot: Shot): boolean {
  for (const e of s.enemies) {
    if (!targetable(e) || shot.struck.includes(e.id)) continue;
    const r = hitHalf(e);
    if (Math.abs(shot.x - e.x) > SHOT.halfW + r || Math.abs(shot.y - e.y) > SHOT.halfH + r) continue;
    count(s, shot);
    damageEnemy(s, e, 1);
    if (!shot.pierce) return true;
    shot.struck.push(e.id);
  }
  const em = s.empress;
  if (em && em.state === 'fighting' && !shot.struck.includes(EMPRESS_ID)
    && Math.abs(shot.x - em.x) <= NOVA.empressHalfW + SHOT.halfW
    && Math.abs(shot.y - em.y) <= NOVA.empressHalfH + SHOT.halfH) {
    count(s, shot);
    hitEmpress(s, 1);
    if (!shot.pierce) return true;
    shot.struck.push(EMPRESS_ID);
  }
  return false;
}

function bombsVsShips(s: GameState): void {
  s.bombs = s.bombs.filter((b) => {
    if (Math.abs(b.y - FIELD.fighterY) > BOMB.halfH + FIGHTER.hitH) return true;
    const xs = shipXs(s);
    for (let i = 0; i < xs.length; i++) {
      if (Math.abs(b.x - (xs[i] ?? 0)) > BOMB.halfW + FIGHTER.hitW) continue;
      hitShip(s, i);
      return false;
    }
    return true;
  });
}

function enemiesVsShips(s: GameState): void {
  const p = s.player;
  for (const e of s.enemies) {
    // One ship down, or a shield just burst: nothing more can touch it this tick.
    if ((p.status !== 'flying' && p.status !== 'arriving') || p.safe > 0) return;
    if (!targetable(e) || e.state === 'formed') continue;
    const r = hitHalf(e) - 1;
    if (Math.abs(e.y - FIELD.fighterY) > r + FIGHTER.hitH) continue;
    const xs = shipXs(s);
    for (let i = 0; i < xs.length; i++) {
      if (Math.abs(e.x - (xs[i] ?? 0)) > r + FIGHTER.hitW) continue;
      // Both go: the enemy scores as if shot (02 §8).
      damageEnemy(s, e, e.hp);
      hitShip(s, i);
      break;
    }
  }
}

function lanceVsShips(s: GameState): void {
  const em = s.empress;
  if (!em || em.lance !== 'fire') return;
  const xs = shipXs(s);
  for (let i = xs.length - 1; i >= 0; i--) {
    if (Math.abs((xs[i] ?? 0) - em.lanceX) <= NOVA.lanceHalf + FIGHTER.hitW) hitShip(s, i);
  }
}

export function collide(s: GameState): void {
  s.shots = s.shots.filter((shot) => !shotStrikes(s, shot));
  const p = s.player;
  // A ship that is safe (a burst shield, a Nova) is passed through, not struck.
  if ((p.status !== 'flying' && p.status !== 'arriving') || p.safe > 0) return;
  bombsVsShips(s);
  enemiesVsShips(s);
  lanceVsShips(s);
}
